import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { jsPDF } from 'jspdf';
import { isEqual } from 'lodash-es';
import moment from 'moment';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import 'svg2pdf.js';

import { RemoteDiagnosticsHelpersService, SvgVddMapping } from '@vi-iot/client';

import { Tab } from '@vi-iot/ui';

import { XMLHelperService } from '@services';
import { HydraulicSchematicStore, RemoteDiagnosticsStore, VddDetailsListStore } from '@store';

import { InformationPopupPage } from '../../../../shared/components/information-popup/information-popup.model';

import { HydraulicSchematicCodeExtractorService } from './hydraulic-schematic-code-extractor.service';
import { SchemeTabs } from './hydraulic-schematic.model';

@Component({
  selector: 'app-hydraulic-schemes',
  templateUrl: './hydraulic-schematic.component.html',
  styleUrls: ['./hydraulic-schematic.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HydraulicSchematicComponent implements OnInit, OnDestroy {
  readonly InformationPopupPage = InformationPopupPage;

  schemeTabs = SchemeTabs;
  gatewaySerial: string;
  svg: SafeHtml;
  isAnyFromHydraulicSchematicVddLoading: boolean;
  svgVdds: SvgVddMapping[];
  showTable: boolean;
  tabs = [
    {
      name: this.translate.instant(
        '+INSTALLATIONS.INSTALLATION.HYDRAULIC_SCHEMATIC.TABS.SYSTEM_SCHEME'
      ),
      active: true,
      value: SchemeTabs.System.toString(),
    },
    {
      name: this.translate.instant(
        '+INSTALLATIONS.INSTALLATION.HYDRAULIC_SCHEMATIC.TABS.INTERNAL_SCHEME'
      ),
      active: false,
      value: SchemeTabs.Internal.toString(),
    },
  ];

  activeTab = this.tabs[0];

  private hydrauliMatrixVddName = 'HydraulicMatrix';
  private matrixCode: string;
  private bundleId: string;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public hydraulicSchematicStore: HydraulicSchematicStore,
    private hydraulicSchematicCodeExtractorService: HydraulicSchematicCodeExtractorService,
    private vddDetailsListStore: VddDetailsListStore,
    public remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private svgHelperService: XMLHelperService,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private remoteDiagnosticsHelpersService: RemoteDiagnosticsHelpersService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((queryParams) => {
      this.gatewaySerial = queryParams?.gatewaySerial;
    });

    this.remoteDiagnosticsStore
      .getMainLogicalLinkFromTopology(this.gatewaySerial)
      .pipe(take(1))
      .subscribe((logicalLink) => {
        this.vddDetailsListStore.getVddSingleValue(
          this.hydrauliMatrixVddName,
          this.gatewaySerial,
          logicalLink
        );

        this.remoteDiagnosticsStore
          .getBundleIdFromTopologyAndHydrauliMatrixVddDetails(
            this.gatewaySerial,
            this.hydrauliMatrixVddName,
            logicalLink
          )
          .pipe(
            filter((value) => !!value.hydraulicMatrixVddDetails),
            distinctUntilChanged(isEqual),
            map((response) => ({
              bundleId: response.bundleId,
              matrixCode: this.hydraulicSchematicCodeExtractorService.extractCodeFromVddRead(
                response.hydraulicMatrixVddDetails
              ),
            })),
            takeUntil(this.ngUnsubscribe$)
          )
          .subscribe((value) => {
            this.matrixCode = value.matrixCode;
            this.bundleId = value.bundleId;

            this.fetchScheme();
          });
      });

    this.hydraulicSchematicStore.downloadHydraulicSchematicMapping();

    this.hydraulicSchematicStore.getHydraulicSchematic$
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((svg) => !!svg),
        map((svgJson) => this.svgHelperService.getXMLStringFromJson(svgJson))
      )
      .subscribe((svg) => {
        this.svg = this.sanitizer.bypassSecurityTrustHtml(svg);

        this.readValues();

        this.cdr.detectChanges();
      });

    this.hydraulicSchematicStore
      .isAnyFromHydraulicSchematicVddLoading()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isAnyFromHydraulicSchematicVddLoading) => {
        this.isAnyFromHydraulicSchematicVddLoading = isAnyFromHydraulicSchematicVddLoading;
        this.cdr.detectChanges();
      });
  }

  readValues(): void {
    this.showTable = true;
    this.hydraulicSchematicStore.triggerReadVddValuesForSvg(this.gatewaySerial);
    this.remoteDiagnosticsStore.lastUserActivityTimestampReset(this.gatewaySerial);
  }

  onTabChange(tab: Tab): void {
    this.remoteDiagnosticsHelpersService.cancelRequests$.next();
    this.activeTab = tab;
    this.fetchScheme();
  }

  async saveAsPdf(): Promise<void> {
    const svgElement = document.getElementById('svgElement');
    const width = (svgElement as any).clientWidth || (svgElement as any).parentNode.clientWidth;
    const height = (svgElement as any).clientHeight || (svgElement as any).parentNode.clientHeight;
    const pdf = new jsPDF(width > height ? 'l' : 'p', 'pt', [width, height]);

    await pdf.svg(svgElement, {
      x: 0,
      y: 0,
      width,
      height: height + 10,
    });

    pdf.save(moment().format('YYYY.MM.DD-hh.mm.ss') + '-schematic.pdf');
  }

  ngOnDestroy(): void {
    this.hydraulicSchematicStore.clearState();
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private fetchScheme(): void {
    if (this.activeTab.value === SchemeTabs.System) {
      return this.hydraulicSchematicStore.downloadHydraulicSchematic(
        this.matrixCode,
        this.bundleId,
        false
      );
    } else {
      return this.hydraulicSchematicStore.downloadHydraulicSchematic(
        this.matrixCode,
        this.bundleId,
        true
      );
    }
  }
}
